import { fg } from '@confluence/feature-gating';
import type { ReplyData } from '@confluence/comments-data/entry-points/useCommentsData';

export const DEFAULT_NUM_VISIBLE_REPLIES = 2;

export enum ViewReplyOptions {
	ALL = 'ALL', // shows all replies
	DEFAULT = 'DEFAULT', // shows up to 3 unread comments
	HIDDEN = 'HIDDEN',
	LIMITED = 'LIMITED',
}

type GetNumOfHiddenRepliesProps = {
	replies: ReplyData[];
	latestReplyIds: Set<string>;
	currentReplyView: ViewReplyOptions;
};

export const getNumOfHiddenReplies = ({
	replies,
	latestReplyIds,
	currentReplyView,
}: GetNumOfHiddenRepliesProps) => {
	switch (currentReplyView) {
		case ViewReplyOptions.HIDDEN:
			return replies.length;
		case ViewReplyOptions.LIMITED:
			return replies.length - latestReplyIds.size;
		default:
			return 0;
	}
};

export const getDefaultHiddenReplies = ({
	replies,
	shouldShowLatestReplies,
	isParentOpen,
	latestReplyIds,
}: {
	replies: ReplyData[];
	shouldShowLatestReplies: boolean;
	isParentOpen: boolean;
	latestReplyIds: Set<string>;
}) =>
	replies.filter((reply) => {
		if (fg('confluence_frontend_new_dangling_comments_ux')) {
			return !shouldShowLatestReplies || !latestReplyIds.has(reply.id);
		}

		return !shouldShowLatestReplies || !isParentOpen ? true : !latestReplyIds.has(reply.id);
	});

export type ProfilePictureInfo = {
	displayName: string;
	profilePicturePath: string | undefined;
};

// filter out duplicate authors when we're showing the profile pictures
export const getProfilePicturesOfHiddenReplies = (
	repliesToFilter: ReplyData[],
): ProfilePictureInfo[] => {
	if (!fg('confluence_frontend_comments_panel_v2')) {
		return [];
	}

	const uniqueAccountsMap = new Map<string, ProfilePictureInfo>();
	// Start from the end of the list and move backwards
	for (let i = repliesToFilter.length - 1; i >= 0; i--) {
		const reply = repliesToFilter[i];
		const { displayName, profilePicture } = reply.author;
		// Determine if the author has an accountId, anonymous users don't have accountId
		let uniqueKey: string;
		if ('accountId' in reply.author && reply.author.accountId) {
			uniqueKey = reply.author.accountId;
		} else {
			// Fallback for anonymous users or if accountId is not present
			uniqueKey = `anonymous-${i}`;
		}
		if (!uniqueAccountsMap.has(uniqueKey)) {
			uniqueAccountsMap.set(uniqueKey, {
				displayName: displayName ?? '',
				profilePicturePath: profilePicture?.path,
			});
		}
	}
	return Array.from(uniqueAccountsMap.values());
};
